import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Check, CheckCircle2, Info, MinusCircle, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { createWorkerSchema } from "@normax-performance/shared-types";
import type { CreateWorkerInput, UpdateWorkerInput, WorkerDto } from "@normax-performance/shared-types";
import { useAuth } from "../../app/providers/AuthProvider";
import { workersApi } from "../../lib/api-client/endpoints";
import { ApiError } from "../../lib/api-client/client";
import {
  Badge,
  Button,
  Input,
  Modal,
  OverflowMenu,
  SortableColumnHeader,
  useSortableRows,
  useToast,
  type OverflowMenuItem,
} from "../../shared-ui/primitives";
import "./WorkersPage.css";

interface WorkerFormValues {
  firstName: string;
  lastName: string;
  employeeCode: string;
}

function WorkerFormModal({
  mode,
  worker,
  onClose,
  onSubmit,
  isPending,
}: {
  mode: "create" | "edit";
  worker?: WorkerDto;
  onClose: () => void;
  onSubmit: (input: CreateWorkerInput) => void;
  isPending: boolean;
}) {
  const { t } = useTranslation("workers");
  const { t: tCommon } = useTranslation("common");
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<WorkerFormValues>({
    resolver: zodResolver(createWorkerSchema),
    defaultValues: {
      firstName: worker?.firstName ?? "",
      lastName: worker?.lastName ?? "",
      employeeCode: worker?.employeeCode ?? "",
    },
  });

  return (
    <Modal
      open
      onOpenChange={(open) => !open && onClose()}
      title={mode === "create" ? t("addWorker") : t("editWorker")}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {tCommon("cancel")}
          </Button>
          <Button type="submit" form="nx-worker-form" variant="primary" loading={isPending}>
            <Check size={16} />
            {tCommon("save")}
          </Button>
        </>
      }
    >
      <form
        id="nx-worker-form"
        onSubmit={handleSubmit((values) =>
          onSubmit({
            firstName: values.firstName,
            lastName: values.lastName,
            employeeCode: values.employeeCode.trim() === "" ? null : values.employeeCode,
          }),
        )}
      >
        <Input
          label={t("field.firstName")}
          required
          error={errors.firstName?.message}
          {...register("firstName")}
        />
        <Input
          label={t("field.lastName")}
          required
          error={errors.lastName?.message}
          {...register("lastName")}
        />
        <Input
          label={t("field.employeeCode")}
          inputMode="numeric"
          error={errors.employeeCode ? t("field.employeeCodeInvalid") : undefined}
          {...register("employeeCode")}
        />
      </form>
    </Modal>
  );
}

type FormState = { mode: "create" } | { mode: "edit"; worker: WorkerDto } | null;

/**
 * Operational MVP Slice A — Workers / Radnici CRUD
 * (NORMAX_PERFORMANCE_OPERATIONAL_MVP_SLICE_A_IMPLEMENTATION_REPORT.md).
 * ADMIN can create/edit/activate/deactivate; DIRECTOR sees the same list
 * read-only — enforced server-side (workers.controller.ts @Roles), this
 * page only mirrors that by not rendering write affordances for DIRECTOR.
 * No hard delete — "deactivate" is `PATCH { active: false }`, matching the
 * schema's existing lifecycle (historical daily_work_entry/work_line rows
 * reference worker_id directly and must never be orphaned).
 */
export function WorkersPage() {
  const { t } = useTranslation("workers");
  const { t: tCommon } = useTranslation("common");
  const { user } = useAuth();
  const isAdmin = user?.role === "ADMIN";
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [formState, setFormState] = useState<FormState>(null);

  const workersQuery = useQuery({
    queryKey: ["workers", "all"],
    queryFn: () => workersApi.list({ includeInactive: true }),
  });

  function handleError(error: unknown) {
    const isCodeConflict =
      error instanceof ApiError && error.code === "CONFLICT" && error.details?.field === "employeeCode";
    showToast({
      title: t("toast.saveErrorTitle"),
      description: isCodeConflict
        ? t("conflict.employeeCode")
        : error instanceof ApiError
          ? error.message
          : t("toast.saveErrorNoConnection"),
      tone: "danger",
    });
  }

  const createMutation = useMutation({
    mutationFn: (input: CreateWorkerInput) => workersApi.create(input),
    onSuccess: () => {
      showToast({ title: t("toast.createSuccess"), tone: "success" });
      void queryClient.invalidateQueries({ queryKey: ["workers"] });
      setFormState(null);
    },
    onError: handleError,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateWorkerInput }) => workersApi.update(id, input),
    onSuccess: (_data, variables) => {
      const keys = Object.keys(variables.input);
      const isStatusToggleOnly = keys.length === 1 && keys[0] === "active";
      showToast({
        title: isStatusToggleOnly
          ? variables.input.active
            ? t("toast.activateSuccess")
            : t("toast.deactivateSuccess")
          : t("toast.updateSuccess"),
        tone: "success",
      });
      void queryClient.invalidateQueries({ queryKey: ["workers"] });
      setFormState(null);
    },
    onError: handleError,
  });

  const workers = workersQuery.data ?? [];
  const isEmpty = !workersQuery.isLoading && !workersQuery.isError && workers.length === 0;
  const isPending = createMutation.isPending || updateMutation.isPending;

  // Default sort: code ascending, null-code rows last — read-only
  // functionality, available to DIRECTOR too
  // (NORMAX_PERFORMANCE_FIXED_WIDTH_CODES_SORTABLE_MASTER_DATA_IMPLEMENTATION_REPORT.md §11-12/§22).
  const { sorted, sortKey, direction, requestSort } = useSortableRows(
    workers,
    {
      code: (w) => w.employeeCode,
      name: (w) => `${w.lastName} ${w.firstName}`,
      status: (w) => (w.active ? 1 : 0),
    },
    { key: "code", direction: "asc" },
  );

  return (
    <div className="nx-workers">
      <div className="nx-workers__header">
        <h1 className="nx-workers__title">{t("title")}</h1>
        {isAdmin ? (
          <Button variant="primary" onClick={() => setFormState({ mode: "create" })}>
            <Plus size={16} />
            {t("addWorker")}
          </Button>
        ) : null}
      </div>

      {!isAdmin ? (
        <div className="nx-workers__notice">
          <Info size={18} aria-hidden="true" />
          <p>{t("readOnlyNotice")}</p>
        </div>
      ) : null}

      {workersQuery.isLoading ? (
        <p className="nx-field__hint">{tCommon("loading")}</p>
      ) : workersQuery.isError ? (
        <p className="nx-workers__error">{t("toast.saveErrorNoConnection")}</p>
      ) : isEmpty ? (
        <div className="nx-workers__empty">
          <p className="nx-workers__empty-title">{t("empty.title")}</p>
          {/* The Add action lives once, in the header above — not duplicated
              here, to avoid two identical buttons on an empty screen. */}
          <p>{isAdmin ? t("empty.adminHint") : t("empty.directorHint")}</p>
        </div>
      ) : (
        <table className="nx-workers__table nx-table">
          <thead>
            <tr>
              <SortableColumnHeader
                label={t("table.employeeCode")}
                sortKey="code"
                activeSortKey={sortKey}
                direction={direction}
                onSort={requestSort}
              />
              <SortableColumnHeader
                label={t("table.name")}
                sortKey="name"
                activeSortKey={sortKey}
                direction={direction}
                onSort={requestSort}
              />
              <SortableColumnHeader
                label={t("table.status")}
                sortKey="status"
                activeSortKey={sortKey}
                direction={direction}
                onSort={requestSort}
              />
              {/* Actions column is intentionally NOT sortable. */}
              {isAdmin ? <th aria-label={t("table.actions")} className="nx-table__actions-col" /> : null}
            </tr>
          </thead>
          <tbody>
            {sorted.map((w) => {
              const actionItems: OverflowMenuItem[] = [
                { key: "edit", label: tCommon("edit"), onClick: () => setFormState({ mode: "edit", worker: w }) },
                {
                  key: "toggle",
                  label: w.active ? tCommon("deactivate") : tCommon("activate"),
                  onClick: () => updateMutation.mutate({ id: w.id, input: { active: !w.active } }),
                },
              ];
              return (
              <tr key={w.id}>
                <td>{w.employeeCode ?? "—"}</td>
                <td>
                  {w.firstName} {w.lastName}
                  {w.hasNamesake ? (
                    <AlertTriangle
                      size={14}
                      className="nx-workers__namesake-icon"
                      aria-label={t("namesakeWarning")}
                    />
                  ) : null}
                </td>
                <td>
                  <Badge
                    tone={w.active ? "success" : "neutral"}
                    icon={w.active ? <CheckCircle2 size={12} aria-hidden /> : <MinusCircle size={12} aria-hidden />}
                  >
                    {w.active ? tCommon("active") : tCommon("inactive")}
                  </Badge>
                </td>
                {isAdmin ? (
                  <td className="nx-table__actions-col">
                    <OverflowMenu items={actionItems} triggerLabel={t("table.actions")} />
                  </td>
                ) : null}
              </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {isAdmin && formState ? (
        <WorkerFormModal
          mode={formState.mode}
          worker={formState.mode === "edit" ? formState.worker : undefined}
          onClose={() => setFormState(null)}
          isPending={isPending}
          onSubmit={(input) => {
            if (formState.mode === "create") {
              createMutation.mutate(input);
            } else {
              updateMutation.mutate({ id: formState.worker.id, input });
            }
          }}
        />
      ) : null}
    </div>
  );
}
